<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { toast } from 'vue3-toastify';
import dartsHubApi from '@/axios/dartsHubApi';
import { backend_url } from '@/config/config';
import { normalizeForSearch } from '@/functions/utils';

interface Rep { email: string; label: string; }
interface Ligue {
  id: number;
  code: string;
  name: string;
  logo?: string | null;
  // Fiche de la ligue (LigueProfile), renvoyée aux seuls rôles admin.
  contact_email?: string | null;
  ligue_postal_code?: string | null;
  ligue_city?: string | null;
  contact_nom?: string | null;
  contact_telephone?: string | null;
  representatives: Rep[];
  premium_events_enabled: boolean;
  premium_events_until?: string | null;
  custom_domain?: string | null;
}

// Lecture seule : les accès premium (et leur durée) se gèrent depuis l'écran unique
// /admin/events-premium, cf. AdminEventsPremium.vue.
const premiumBadge = (l: { premium_events_enabled: boolean; premium_events_until?: string | null }) => {
  if (!l.premium_events_enabled) return { label: 'Standard', cls: 'badge-gray' };
  if (!l.premium_events_until) return { label: 'Premium · sans limite', cls: 'badge-green' };
  const until = new Date(l.premium_events_until);
  const expired = until < new Date(new Date().toDateString());
  return { label: `${expired ? 'Expiré le' : 'Premium jusqu\'au'} ${until.toLocaleDateString('fr-FR')}`, cls: expired ? 'badge-red' : 'badge-green' };
};

const logoUrl = (logo?: string | null) => (logo?.startsWith('/uploads/') ? `${backend_url}${logo}` : logo || '');

const ligues = ref<Ligue[]>([]);
const loading = ref(true);
const search = ref('');

const showRepModal = ref(false);
const repLigue = ref<Ligue | null>(null);
const newRepEmail = ref('');
const savingRep = ref(false);

// Création/modification (super admin). Code figé après création : recopié en texte sur
// les événements et les licences (cf. clubs/schemas.py::OrgUpdate).
const showFormModal = ref(false);
const formLigue = ref<Ligue | null>(null);
const emptyForm = () => ({
  code: '', name: '', contact_email: '', postal_code: '', city: '', contact_nom: '', contact_telephone: '',
  custom_domain: '',
});
const form = ref(emptyForm());
const formLogoFile = ref<File | null>(null);
const savingForm = ref(false);

const openFormModal = (ligue: Ligue | null) => {
  formLigue.value = ligue;
  form.value = ligue
    ? {
        code: ligue.code, name: ligue.name,
        contact_email: ligue.contact_email || '', postal_code: ligue.ligue_postal_code || '', city: ligue.ligue_city || '',
        contact_nom: ligue.contact_nom || '', contact_telephone: ligue.contact_telephone || '',
        custom_domain: ligue.custom_domain || '',
      }
    : emptyForm();
  formLogoFile.value = null;
  showFormModal.value = true;
};

const saveForm = async () => {
  if (!form.value.name.trim() || (!formLigue.value && !form.value.code.trim())) {
    toast.error('Code et nom requis.');
    return;
  }
  savingForm.value = true;
  const { code, custom_domain, ...fields } = form.value;
  try {
    const { data } = formLigue.value
      ? await dartsHubApi.patch(`/clubs/ligues/${formLigue.value.id}`, fields)
      : await dartsHubApi.post('/clubs/ligues', { code, ...fields });
    // Domaine propre (ex. licence.lsef.fr) : à brancher aussi côté serveur (DNS, Traefik).
    if (custom_domain.trim() !== (formLigue.value?.custom_domain || '')) {
      try {
        await dartsHubApi.patch(`/branding/ligues/${data.id}`, { custom_domain: custom_domain.trim() || null });
      } catch (err: any) {
        toast.warning(`Ligue enregistrée, mais pas le domaine : ${err.response?.data?.detail || 'erreur'}`);
      }
    }
    if (formLogoFile.value) {
      const fd = new FormData();
      fd.append('file', formLogoFile.value);
      try {
        await dartsHubApi.post(`/clubs/ligues/${data.id}/upload-logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } catch {
        toast.warning('Ligue enregistrée, mais l\'envoi du logo a échoué.');
      }
    }
    toast.success(formLigue.value ? 'Ligue mise à jour.' : 'Ligue créée.');
    showFormModal.value = false;
    await load();
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur.');
  } finally {
    savingForm.value = false;
  }
};

const filtered = computed(() => {
  const q = normalizeForSearch(search.value);
  if (!q) return ligues.value;
  return ligues.value.filter(l => normalizeForSearch(l.name).includes(q) || normalizeForSearch(l.code).includes(q));
});

const load = async () => {
  loading.value = true;
  try {
    const { data } = await dartsHubApi.get('/clubs/ligues');
    ligues.value = data;
  } catch {
    toast.error('Erreur de chargement.');
  } finally {
    loading.value = false;
  }
};

const openRepModal = (ligue: Ligue) => {
  repLigue.value = ligue;
  newRepEmail.value = '';
  showRepModal.value = true;
};

const addRep = async () => {
  if (!newRepEmail.value || !repLigue.value) return;
  savingRep.value = true;
  try {
    await dartsHubApi.post('/clubs/ligue-representatives', {
      user_email: newRepEmail.value,
      ligue_id: repLigue.value.id,
    });
    toast.success('Représentant assigné.');
    newRepEmail.value = '';
    await load();
    repLigue.value = ligues.value.find(l => l.id === repLigue.value!.id) ?? null;
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur.');
  } finally {
    savingRep.value = false;
  }
};

const removeRep = async (email: string) => {
  try {
    await dartsHubApi.delete(`/clubs/ligue-representatives/${encodeURIComponent(email)}`);
    toast.success('Représentant retiré.');
    await load();
    if (repLigue.value) repLigue.value = ligues.value.find(l => l.id === repLigue.value!.id) ?? null;
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur.');
  }
};

onMounted(load);
</script>

<template>
  <div class="page">
    <div class="page-header">
      <div style="display:flex;align-items:center;gap:0.75rem;flex-wrap:wrap">
        <h1><i class="fa-solid fa-flag" /> Ligues</h1>
        <button class="btn btn-primary btn-sm" @click="openFormModal(null)">
          <i class="fa-solid fa-plus" /> Créer une ligue
        </button>
      </div>
      <div class="filters">
        <div class="search-bar">
          <i class="fa-solid fa-magnifying-glass" style="color:var(--text-muted)" />
          <input v-model="search" placeholder="Nom ou code…" />
        </div>
      </div>
    </div>

    <div v-if="loading" class="loader"><i class="fa-solid fa-spinner fa-spin" /> Chargement…</div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>Code</th>
            <th>Logo</th>
            <th>Nom</th>
            <th>Contact</th>
            <th>Premium</th>
            <th>Représentants</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="ligue in filtered" :key="ligue.id">
            <td><span class="badge badge-gray">{{ ligue.code }}</span></td>
            <td>
              <img v-if="ligue.logo" :src="logoUrl(ligue.logo)" class="org-logo" alt="logo" />
              <span v-else class="badge badge-gray">-</span>
            </td>
            <td class="club-name">
              {{ ligue.name }}
              <div v-if="ligue.ligue_city" class="muted-line">{{ ligue.ligue_postal_code }} {{ ligue.ligue_city }}</div>
            </td>
            <td>
              <template v-if="ligue.contact_email || ligue.contact_nom || ligue.contact_telephone">
                <div v-if="ligue.contact_email">{{ ligue.contact_email }}</div>
                <div v-if="ligue.contact_nom || ligue.contact_telephone" class="muted-line">
                  {{ [ligue.contact_nom, ligue.contact_telephone].filter(Boolean).join(' · ') }}
                </div>
              </template>
              <span v-else class="badge badge-gray">-</span>
            </td>
            <td>
              <router-link to="/admin/events-premium" class="badge" :class="premiumBadge(ligue).cls"
                title="Gérer les accès premium (écran unique)">
                {{ premiumBadge(ligue).label }}
              </router-link>
            </td>
            <td>
              <div v-if="ligue.representatives.length" style="display:flex;flex-wrap:wrap;gap:0.25rem">
                <span v-for="rep in ligue.representatives" class="badge badge-blue" :title="rep.email" :key="rep.email">
                  {{ rep.label }}
                </span>
              </div>
              <span v-else class="badge badge-gray">-</span>
            </td>
            <td style="display:flex;gap:0.25rem">
              <button class="btn btn-ghost btn-sm" title="Modifier" @click="openFormModal(ligue)">
                <i class="fa-solid fa-pen" />
              </button>
              <button class="btn btn-ghost btn-sm" title="Gérer les représentants" @click="openRepModal(ligue)">
                <i class="fa-solid fa-user-tie" />
              </button>
            </td>
          </tr>
          <tr v-if="filtered.length === 0">
            <td colspan="7" style="text-align:center;color:var(--text-muted)">Aucune ligue trouvée</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="!loading" class="mobile-cards">
      <div v-for="ligue in filtered" class="mobile-card" :key="ligue.id">
        <div class="card-top">
          <div style="display:flex;align-items:center;gap:0.6rem">
            <img v-if="ligue.logo" :src="logoUrl(ligue.logo)" class="org-logo" alt="logo" />
            <div>
              <div style="font-weight:600;font-size:0.95rem">{{ ligue.name }}</div>
              <div style="font-size:0.78rem;color:var(--text-muted)">
                {{ ligue.code }}<template v-if="ligue.ligue_city"> · {{ ligue.ligue_city }}</template>
              </div>
            </div>
          </div>
          <div style="display:flex;gap:0.25rem">
            <button class="btn btn-ghost btn-sm" title="Modifier" @click="openFormModal(ligue)">
              <i class="fa-solid fa-pen" />
            </button>
            <button class="btn btn-ghost btn-sm" title="Gérer les représentants" @click="openRepModal(ligue)">
              <i class="fa-solid fa-user-tie" />
            </button>
          </div>
        </div>
        <div v-if="ligue.contact_email" class="muted-line">
          <i class="fa-solid fa-envelope" /> {{ ligue.contact_email }}
        </div>
        <div v-if="ligue.contact_nom || ligue.contact_telephone" class="muted-line">
          <i class="fa-solid fa-user" /> {{ [ligue.contact_nom, ligue.contact_telephone].filter(Boolean).join(' · ') }}
        </div>
        <div>
          <router-link to="/admin/events-premium" class="badge" :class="premiumBadge(ligue).cls">
            {{ premiumBadge(ligue).label }}
          </router-link>
        </div>
        <div v-if="ligue.representatives.length" style="display:flex;flex-wrap:wrap;gap:0.25rem">
          <span v-for="rep in ligue.representatives" class="badge badge-blue" :title="rep.email" :key="rep.email">{{ rep.label }}</span>
        </div>
        <div v-else class="badge badge-gray" style="width:fit-content">Aucun représentant</div>
      </div>
      <div v-if="filtered.length === 0" style="text-align:center;color:var(--text-muted);padding:1.5rem">Aucune ligue trouvée</div>
    </div>

    <div v-if="!loading" class="table-count">
      {{ filtered.length }} / {{ ligues.length }} ligues
    </div>

    <!-- Modal création / modification -->
    <div v-if="showFormModal" class="modal-overlay" @click.self="showFormModal = false">
      <div class="modal">
        <div class="modal-header">
          <h2>{{ formLigue ? `Modifier la ligue ${formLigue.code}` : 'Créer une ligue' }}</h2>
          <button class="btn btn-ghost btn-sm" @click="showFormModal = false"><i class="fa-solid fa-xmark" /></button>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label>Code</label>
            <input v-model="form.code" type="text" maxlength="6" placeholder="ex: SDE" :disabled="!!formLigue"
              :title="formLigue ? 'Non modifiable : repris sur les événements et les licences' : ''" />
          </div>
          <div class="form-group">
            <label>Nom</label>
            <input v-model="form.name" type="text" placeholder="Nom de la ligue" />
          </div>
        </div>
        <div class="form-group">
          <label>Adresse de contact (email)</label>
          <input v-model="form.contact_email" type="email" placeholder="contact@ligue.fr" />
        </div>
        <div class="form-row">
          <div class="form-group">
            <label>Code postal</label>
            <input v-model="form.postal_code" type="text" inputmode="numeric" maxlength="5" />
          </div>
          <div class="form-group">
            <label>Ville</label>
            <input v-model="form.city" type="text" />
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label>Contact</label>
            <input v-model="form.contact_nom" type="text" placeholder="Nom" />
          </div>
          <div class="form-group">
            <label>Téléphone</label>
            <input v-model="form.contact_telephone" type="tel" />
          </div>
        </div>
        <div class="form-group">
          <label>Adresse dédiée de l'espace licences</label>
          <input v-model="form.custom_domain" type="text" placeholder="ex: licence.mondomaine.fr" />
          <small class="muted-line">Facultatif. Sans adresse dédiée : licence.dartshub.fr. Le domaine doit aussi être branché sur le serveur.</small>
        </div>
        <div class="form-group">
          <label>Logo</label>
          <input type="file" accept="image/*" @change="(e) => formLogoFile = (e.target as HTMLInputElement).files?.[0] ?? null" />
        </div>
        <div class="form-actions">
          <button class="btn btn-ghost btn-sm" @click="showFormModal = false">Annuler</button>
          <button class="btn btn-primary btn-sm" :disabled="savingForm" @click="saveForm">
            <i v-if="savingForm" class="fa-solid fa-spinner fa-spin" />
            {{ formLigue ? 'Enregistrer' : 'Créer' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal représentants -->
    <div v-if="showRepModal" class="modal-overlay" @click.self="showRepModal = false">
      <div class="modal">
        <div class="modal-header">
          <h2>Représentants : {{ repLigue?.name }}</h2>
          <button class="btn btn-ghost btn-sm" @click="showRepModal = false"><i class="fa-solid fa-xmark" /></button>
        </div>
        <div class="form-group">
          <div class="reps-list">
            <div v-if="!repLigue?.representatives.length" style="color:var(--text-muted);font-size:0.8rem">Aucun représentant</div>
            <div v-for="rep in repLigue?.representatives" class="rep-row" :key="rep.email">
              <span class="badge badge-blue" :title="rep.email">{{ rep.label }}</span>
              <button class="btn btn-ghost btn-sm" title="Retirer" @click="removeRep(rep.email)">
                <i class="fa-solid fa-xmark" />
              </button>
            </div>
          </div>
          <div class="rep-add-row">
            <input v-model="newRepEmail" type="email" placeholder="email@exemple.fr" @keyup.enter="addRep" />
            <button class="btn btn-primary btn-sm" :disabled="savingRep || !newRepEmail" @click="addRep">
              <i v-if="savingRep" class="fa-solid fa-spinner fa-spin" /><i v-else class="fa-solid fa-plus" />
            </button>
          </div>
        </div>
        <div class="form-actions">
          <button class="btn btn-ghost btn-sm" @click="showRepModal = false">Fermer</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.filters {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: 1rem;
  overflow-y: auto;
}

.modal {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 1.5rem;
  min-width: 0;
  max-width: 480px;
  width: 100%;
  max-height: calc(100dvh - 2rem);
  overflow-y: auto;
  margin: auto;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
}

.modal-header h2 {
  font-size: 1rem;
  font-weight: 600;
}

.club-name {
  font-weight: 500;
}

.form-row {
  display: grid;
  grid-template-columns: 7rem 1fr;
  gap: 0.75rem;
}

.muted-line {
  font-size: 0.78rem;
  font-weight: 400;
  color: var(--text-muted);
}

.org-logo {
  width: 32px;
  height: 32px;
  object-fit: contain;
  border-radius: 4px;
  border: 1px solid var(--border);
  background: var(--bg-card);
}

.table-count {
  margin-top: 0.75rem;
  color: var(--text-muted);
  font-size: 0.8rem;
}

.reps-list {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
}

.rep-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.rep-add-row {
  display: flex;
  gap: 0.5rem;
}

.rep-add-row input {
  flex: 1;
}

.badge-blue {
  background: rgba(59, 130, 246, 0.15);
  color: #2563eb;
  border: 1px solid rgba(59, 130, 246, 0.3);
}

.mobile-cards { display: none; }

@media (max-width: 640px) {
  .table-wrapper { display: none; }
  .mobile-cards { display: flex; flex-direction: column; gap: 0.75rem; }
}

.mobile-card {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 0.875rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
}
</style>
